import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import "../css/Sidebar.css";
import LogoSenaGestor from "../image/LogoSenaGestor.png";
import DropdownMenu from "./DropdownMenu";

const Sidebar = ({ sidebarRef, isOpen, onCloseSidebar }) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const normalizarTexto = (texto = "") =>
    texto
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "");

  const leerStorage = (key, valorDefault = {}) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : valorDefault;
    } catch (error) {
      return valorDefault;
    }
  };

  const usuariosStorage = leerStorage("usuarios", {});
  const usuarioStorage = leerStorage("usuario", {});
  const userStorage = leerStorage("user", {});
  const permisosStorage = leerStorage("permisos", {});

  const textoSesion = normalizarTexto(
    JSON.stringify({
      usuariosStorage,
      usuarioStorage,
      userStorage,
      permisosStorage,
    })
  );

  const esSuperAdmin =
    textoSesion.includes("superadmin") ||
    textoSesion.includes("super_admin");

  const modulosPermisos =
    permisosStorage?.modulos ||
    permisosStorage?.permisos ||
    usuariosStorage?.modulos ||
    usuariosStorage?.permisos ||
    usuarioStorage?.modulos ||
    usuarioStorage?.permisos ||
    userStorage?.modulos ||
    userStorage?.permisos ||
    [];

  const puedeVerModulo = (nombreModulo) => {
    if (esSuperAdmin) return true;

    const moduloBuscado = normalizarTexto(nombreModulo);

    const modulo = Array.isArray(modulosPermisos)
      ? modulosPermisos.find((item) => {
          const nombre =
            item?.nombreModulo ||
            item?.modulo ||
            item?.nombre ||
            item?.name ||
            item?.moduloObjectId?.nombreModulo ||
            item?.moduloObjectId?.nombre ||
            "";

          return normalizarTexto(nombre) === moduloBuscado;
        })
      : null;

    return (
      modulo?.puedeLeer === true ||
      modulo?.leer === true ||
      modulo?.read === true
    );
  };

  const linksInstitucional = [
    puedeVerModulo("Regionales") && {
      to: "/institucional/regional/",
      label: "Regional",
      iconClass: "fas fa-user",
    },
    puedeVerModulo("Centros") && {
      to: "/institucional/centro/",
      label: "Centro",
      iconClass: "fas fa-shield-alt",
    },
    puedeVerModulo("ProgramasFormacion") && {
      to: "/institucional/programas/",
      label: "Programa",
      iconClass: "fas fa-bell",
    },
    puedeVerModulo("Grupos") && {
      to: "/institucional/grupos/",
      label: "Grupos",
      iconClass: "fas fa-bell",
    },
  ].filter(Boolean);

  const linksEnteConformador = [
    puedeVerModulo("EntesConformadores") && {
      to: "/enteconformador/ente/",
      label: "Listar Entes",
      iconClass: "fas fa-shield-alt",
    },
    puedeVerModulo("JefesInmediatos") && {
      to: "/enteconformador/jefes/",
      label: "Listar Jefes",
      iconClass: "fas fa-bell",
    },
  ].filter(Boolean);

  const linksMatricula = [
    puedeVerModulo("Matriculas") && {
      to: "/matricula/listarmatriculas/",
      label: "Listar Matriculas",
      iconClass: "fa-solid fa-list-check",
    },
    puedeVerModulo("TarjetaUID") && {
      to: "/tarjeta/listartarjetaaprendiz/",
      label: "Tarjeta UID",
      iconClass: "fa-solid fa-id-card",
    },
    puedeVerModulo("EtapaProductiva") && {
      to: "/etapaproductiva/listareproductiva/",
      label: "Etapa Productiva",
      iconClass: "fa-solid fa-briefcase",
    },
  ].filter(Boolean);

  const linksReportes = [
    puedeVerModulo("Reportes") && {
      to: "/reportes",
      label: "Diarios",
      iconClass: "fas fa-calendar-day",
    },
  ].filter(Boolean);

  const linksConfiguraciones = [
    puedeVerModulo("Notificaciones") && {
      to: "/configuraciones/notificaciones",
      label: "Notificaciones",
      iconClass: "fas fa-bell",
    },
    puedeVerModulo("RolesyPermisos") && {
      to: "/configuraciones/asignarmodulos",
      label: "Roles y Permisos",
      iconClass: "fas fa-user-shield",
    },
  ].filter(Boolean);

  const toggleDropdown = (dropdownId) => {
    setActiveDropdown(activeDropdown === dropdownId ? null : dropdownId);
  };

  const handleLinkClick = () => {
    if (window.innerWidth <= 767.98) {
      onCloseSidebar?.();
    }
  };

  useEffect(() => {
    let timeout;

    const handleOutsideClick = (e) => {
      if (
        isOpen &&
        sidebarRef?.current &&
        !sidebarRef.current.contains(e.target) &&
        window.innerWidth <= 767.98
      ) {
        onCloseSidebar?.();
      }
    };

    if (isOpen) {
      timeout = setTimeout(() => {
        document.addEventListener("mousedown", handleOutsideClick);
      }, 100);
    }

    return () => {
      clearTimeout(timeout);
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onCloseSidebar, sidebarRef]);

  useEffect(() => {
    if (window.innerWidth <= 767.98 && isOpen) {
      onCloseSidebar?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleLogout = () => {
    localStorage.removeItem("usuarios");
    localStorage.removeItem("usuario");
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("permisos");

    navigate("/", { replace: true });
  };

  return (
    <nav
      id="sidebarMenu"
      ref={sidebarRef}
      className={`sidebar-custom d-md-block ${
        isOpen ? "show-mobile" : "d-none d-md-block"
      }`}
    >
      <div className="position-sticky pt-4 h-100 d-flex flex-column">
        <div className="sidebar-header text-center mb-4">
          <img src={LogoSenaGestor} alt="Logo" style={{ height: "200px" }} />
        </div>

        <div className="text-center d-md-none mb-3">
          <button className="btn btn-outline-light" onClick={onCloseSidebar}>
            Cerrar menú
          </button>
        </div>

        <ul className="nav flex-column mb-auto">
          <li className="nav-item">
            <Link
              to="/inicio"
              onClick={handleLinkClick}
              className="nav-link text-white d-flex align-items-center"
            >
              <i className="fas fa-home me-2"></i>
              <span>Inicio</span>
            </Link>
          </li>

          <li className="nav-item">
            <Link
              to="/perfil"
              onClick={handleLinkClick}
              className="nav-link text-white d-flex align-items-center"
            >
              <i className="fas fa-user me-2"></i>
              <span>Mi Perfil</span>
            </Link>
          </li>

          {puedeVerModulo("Instructores") && (
            <NavLink
              to="/instructor/"
              onClick={handleLinkClick}
              className={({ isActive }) =>
                `nav-link d-flex align-items-center gap-2 px-3 py-2 rounded-3 text-white ${
                  isActive ? "active fw-semibold" : ""
                }`
              }
              title="Instructor"
            >
              <i className="fa-solid fa-chalkboard-user"></i>
              <span>Instructor</span>
            </NavLink>
          )}

          {puedeVerModulo("Aprendices") && (
            <NavLink
              to="/aprendiz/"
              onClick={handleLinkClick}
              className={({ isActive }) =>
                `nav-link d-flex align-items-center gap-2 px-3 py-2 rounded-3 text-white ${
                  isActive ? "active fw-semibold" : ""
                }`
              }
              title="Aprendices"
            >
              <i className="fa-solid fa-graduation-cap"></i>
              <span>Aprendices</span>
            </NavLink>
          )}

          {linksInstitucional.length > 0 && (
            <DropdownMenu
              id="institucionalDropdown"
              iconClass="fa-solid fa-building-columns"
              label="Institucional"
              submenuId="submenuInstitucional"
              isOpen={activeDropdown === "institucionalDropdown"}
              toggleDropdown={() => toggleDropdown("institucionalDropdown")}
              links={linksInstitucional}
              onLinkClick={handleLinkClick}
            />
          )}

          {linksEnteConformador.length > 0 && (
            <DropdownMenu
              id="enteConformadorDropdown"
              iconClass="fa-solid fa-school"
              label="Ente Conformador"
              submenuId="submenuEnteConformador"
              isOpen={activeDropdown === "enteConformadorDropdown"}
              toggleDropdown={() => toggleDropdown("enteConformadorDropdown")}
              links={linksEnteConformador}
              onLinkClick={handleLinkClick}
            />
          )}

          {linksMatricula.length > 0 && (
            <DropdownMenu
              id="matriculaDropdown"
              iconClass="fa-solid fa-user-graduate"
              label="Matricula"
              submenuId="submenuMatricula"
              isOpen={activeDropdown === "matriculaDropdown"}
              toggleDropdown={() => toggleDropdown("matriculaDropdown")}
              links={linksMatricula}
              onLinkClick={handleLinkClick}
            />
          )}

          {linksReportes.length > 0 && (
            <DropdownMenu
              id="reportesDropdown"
              iconClass="fas fa-chart-line"
              label="Reportes"
              submenuId="submenuReportes"
              isOpen={activeDropdown === "reportesDropdown"}
              toggleDropdown={() => toggleDropdown("reportesDropdown")}
              links={linksReportes}
              onLinkClick={handleLinkClick}
            />
          )}

          {linksConfiguraciones.length > 0 && (
            <DropdownMenu
              id="configuracionDropdown"
              iconClass="fas fa-cog"
              label="Configuraciones"
              submenuId="submenuConfiguraciones"
              isOpen={activeDropdown === "configuracionDropdown"}
              toggleDropdown={() => toggleDropdown("configuracionDropdown")}
              links={linksConfiguraciones}
              onLinkClick={handleLinkClick}
            />
          )}

          <li className="nav-item">
            <Link
              className="nav-link text-white d-flex align-items-center"
              to="/ayuda"
              onClick={handleLinkClick}
            >
              <i className="fa-solid fa-handshake-angle me-2"></i>
              <span>Ayuda</span>
            </Link>
          </li>
        </ul>

        <div className="mt-auto d-flex justify-content-center mb-3">
          <button
            className="btn btn-outline-light"
            type="button"
            onClick={handleLogout}
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Sidebar;